/**
 * Fase 4.1 — Reports pagina.
 * Rustige, editorial layout met:
 *   • Filterbalk (periode + medewerker + team + locatie + type + status)
 *   • Live statistieken die exact de actieve filters volgen
 *   • Resultatenlijst (tabel desktop / cards mobiel) met dezelfde scope
 *   • CSV-export gebruikt exact dezelfde filters
 * Alle berekeningen (o.a. verzuimdagen binnen periode) komen server-side
 * uit /api/reports/leave-absence — één bron voor JSON, stats, UI en CSV.
 */
import { useEffect, useMemo, useState, useCallback } from "react";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";
import { PageHeader } from "@/components/ui-bits";
import LocationScopeSelect from "@/components/LocationScopeSelect";
import { displayEmployeeName } from "@/lib/employeeName";
import {
  Download, Clock, CalendarDays, MapPin, Building2, Filter, Users as UsersIcon,
  ClipboardList, Thermometer, CheckCircle2, XCircle, AlarmClock,
} from "lucide-react";
import { toast } from "sonner";

function fmt(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
function startOfMonth() {
  const d = new Date();
  return fmt(new Date(d.getFullYear(), d.getMonth(), 1));
}
function endOfMonth() {
  const d = new Date();
  return fmt(new Date(d.getFullYear(), d.getMonth() + 1, 0));
}
function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso + "T00:00:00").toLocaleDateString("nl", { day: "numeric", month: "short", year: "numeric" }); }
  catch { return iso; }
}
function statusStyle(s) {
  switch (s) {
    case "pending":  return { bg: "hsl(38 92% 94%)",  fg: "hsl(28 82% 40%)" };
    case "approved": return { bg: "hsl(150 55% 92%)", fg: "hsl(150 55% 30%)" };
    case "rejected": return { bg: "hsl(0 65% 95%)",   fg: "hsl(0 65% 42%)" };
    default:         return { bg: "hsl(220 12% 94%)", fg: "hsl(220 12% 38%)" };
  }
}

async function downloadReport(url, filename, t) {
  try {
    const res = await api.get(url, { responseType: "blob" });
    const blob = new Blob([res.data], { type: "text/csv;charset=utf-8" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(link.href);
    toast.success(t("report_downloaded"));
  } catch (e) {
    toast.error(e.response?.data?.detail || t("error"));
  }
}

export default function Reports() {
  const { t } = useI18n();

  // Base data
  const [locations, setLocations] = useState([]);
  const [teams, setTeams] = useState([]);
  const [employees, setEmployees] = useState([]);

  // Filters (shared across leave/timesheets/shifts + CSV)
  const [start, setStart] = useState(startOfMonth());
  const [end, setEnd] = useState(endOfMonth());
  const [locId, setLocId] = useState("");
  const [teamId, setTeamId] = useState("");
  const [empId, setEmpId] = useState("");
  const [leaveType, setLeaveType] = useState("");
  const [leaveStatus, setLeaveStatus] = useState("");

  // Legacy report status filters (timesheets/shifts)
  const [tsStatus, setTsStatus] = useState("approved");
  const [shStatus, setShStatus] = useState("all");
  const [busy, setBusy] = useState(null);

  // Live report state
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      const [l, tm, u] = await Promise.all([
        api.get("/locations"),
        api.get("/teams"),
        api.get("/users?role=employee"),
      ]);
      setLocations(l.data);
      setTeams(tm.data);
      setEmployees(u.data);
    })();
  }, []);

  // Filter cascade — when the active location changes, invalidate any team
  // or employee selection that no longer belongs to the visible scope.
  useEffect(() => {
    if (!locId) return;
    if (teamId) {
      const tm = teams.find((x) => x._id === teamId);
      if (tm && tm.location_id && tm.location_id !== locId) setTeamId("");
    }
    if (empId) {
      const em = employees.find((x) => x._id === empId);
      if (em && em.location_id && em.location_id !== locId) setEmpId("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locId]);

  // When team changes, if the selected employee doesn't belong → reset.
  useEffect(() => {
    if (!teamId || !empId) return;
    const em = employees.find((x) => x._id === empId);
    if (em && em.team_id && em.team_id !== teamId) setEmpId("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamId]);

  const visibleTeams = useMemo(() => {
    if (!locId) return teams;
    return teams.filter((tm) => !tm.location_id || tm.location_id === locId);
  }, [teams, locId]);

  const visibleEmployees = useMemo(() => {
    let list = employees;
    if (locId) list = list.filter((u) => !u.location_id || u.location_id === locId);
    if (teamId) list = list.filter((u) => !u.team_id || u.team_id === teamId);
    return list;
  }, [employees, locId, teamId]);

  // Query-string builder — shared by both the live fetch and the CSV export.
  const buildQS = useCallback(() => {
    const qs = new URLSearchParams({ start, end });
    if (empId)       qs.set("employee_id", empId);
    if (locId)       qs.set("location_id", locId);
    if (teamId)      qs.set("team_id", teamId);
    if (leaveType)   qs.set("leave_type", leaveType);
    if (leaveStatus) qs.set("status", leaveStatus);
    return qs;
  }, [start, end, empId, locId, teamId, leaveType, leaveStatus]);

  // Fetch live stats + results whenever any filter changes.
  useEffect(() => {
    let cancel = false;
    (async () => {
      setLoading(true); setError(null);
      try {
        const qs = buildQS();
        const res = await api.get(`/reports/leave-absence?${qs.toString()}`);
        if (!cancel) setReport(res.data);
      } catch (e) {
        if (!cancel) { setError(e.response?.data?.detail || t("rep_results_error")); setReport(null); }
      } finally { if (!cancel) setLoading(false); }
    })();
    return () => { cancel = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [start, end, empId, locId, teamId, leaveType, leaveStatus]);

  const stats = report?.stats;
  const rows = report?.rows || [];

  const dlLeaveCsv = async () => {
    const qs = buildQS();
    qs.set("lang", "nl");
    await downloadReport(
      `/reports/leave-absence.csv?${qs.toString()}`,
      `rostr-verlof-verzuim-${start.slice(0, 7)}.csv`,
      t,
    );
  };
  const dlTimesheets = async () => {
    setBusy("ts");
    const qs = new URLSearchParams({ start, end, status: tsStatus });
    if (empId) qs.set("employee_id", empId);
    if (locId) qs.set("location_id", locId);
    if (teamId) qs.set("team_id", teamId);
    await downloadReport(`/reports/timesheets?${qs.toString()}`, `uren_${start}_${end}.csv`, t);
    setBusy(null);
  };
  const dlShifts = async () => {
    setBusy("sh");
    const qs = new URLSearchParams({ start, end });
    if (locId) qs.set("location_id", locId);
    if (teamId) qs.set("team_id", teamId);
    if (shStatus && shStatus !== "all") qs.set("status", shStatus);
    await downloadReport(`/reports/shifts?${qs.toString()}`, `rooster_${start}_${end}.csv`, t);
    setBusy(null);
  };

  return (
    <div data-testid="page-reports" className="max-w-5xl">
      <PageHeader
        eyebrow={t("reports_label")}
        title={t("reports_page_title")}
        subtitle={t("reports_page_subtitle")}
      />

      {/* Filters — shared for leave-absence live view + timesheets + shifts + CSV */}
      <motion.section
        initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
        className="card p-5 md:p-6 mb-4"
        data-testid="rep-filters"
      >
        <div className="flex items-center gap-2 eyebrow mb-4">
          <Filter size={12} strokeWidth={2} /> {t("filter")}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
          <label className="flex flex-col gap-1.5">
            <span className="text-[12px] font-medium text-[hsl(var(--text-muted))] flex items-center gap-1.5">
              <CalendarDays size={12} strokeWidth={2} /> {t("from_date")}
            </span>
            <input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="input-field" data-testid="rep-start" />
          </label>
          <label className="flex flex-col gap-1.5">
            <span className="text-[12px] font-medium text-[hsl(var(--text-muted))] flex items-center gap-1.5">
              <CalendarDays size={12} strokeWidth={2} /> {t("until_date")}
            </span>
            <input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="input-field" data-testid="rep-end" />
          </label>
          <label className="flex flex-col gap-1.5">
            <span className="text-[12px] font-medium text-[hsl(var(--text-muted))] flex items-center gap-1.5">
              <MapPin size={12} strokeWidth={2} /> {t("location")}
            </span>
            {locations.length > 1 ? (
              <LocationScopeSelect
                variant="input"
                allowAll={true}
                locations={locations}
                value={locId}
                onChange={setLocId}
                allLabel={t("all_locations")}
                testid="rep-loc"
                ariaLabel={t("location")}
              />
            ) : (
              <div
                data-testid="rep-loc-single"
                className="input-field flex items-center text-[hsl(var(--text-muted))]"
              >
                {locations[0]?.name || t("all_locations")}
              </div>
            )}
          </label>
          <label className="flex flex-col gap-1.5">
            <span className="text-[12px] font-medium text-[hsl(var(--text-muted))] flex items-center gap-1.5">
              <Building2 size={12} strokeWidth={2} /> {t("team")}
            </span>
            <select value={teamId} onChange={(e) => setTeamId(e.target.value)} className="input-field" data-testid="rep-team">
              <option value="">{t("all")}</option>
              {visibleTeams.map((tm) => (<option key={tm._id} value={tm._id}>{tm.name}</option>))}
            </select>
          </label>
          <label className="flex flex-col gap-1.5">
            <span className="text-[12px] font-medium text-[hsl(var(--text-muted))]">{t("rep_filter_employee")}</span>
            <select value={empId} onChange={(e) => setEmpId(e.target.value)} className="input-field" data-testid="rep-emp">
              <option value="">{t("all")}</option>
              {visibleEmployees.map((u) => (<option key={u._id} value={u._id}>{u.name}</option>))}
            </select>
          </label>
          <label className="flex flex-col gap-1.5">
            <span className="text-[12px] font-medium text-[hsl(var(--text-muted))]">{t("rep_filter_type")}</span>
            <select value={leaveType} onChange={(e) => setLeaveType(e.target.value)} className="input-field" data-testid="rep-leave-type">
              <option value="">{t("all")}</option>
              <option value="vacation">{t("vacation")}</option>
              <option value="sick">{t("sick")}</option>
              <option value="personal">{t("personal")}</option>
              <option value="unpaid">{t("unpaid")}</option>
              <option value="other">{t("other")}</option>
            </select>
          </label>
          <label className="flex flex-col gap-1.5">
            <span className="text-[12px] font-medium text-[hsl(var(--text-muted))]">{t("rep_filter_status")}</span>
            <select value={leaveStatus} onChange={(e) => setLeaveStatus(e.target.value)} className="input-field" data-testid="rep-leave-status">
              <option value="">{t("all")}</option>
              <option value="pending">{t("status_pending")}</option>
              <option value="approved">{t("status_approved")}</option>
              <option value="rejected">{t("status_rejected")}</option>
            </select>
          </label>
        </div>
      </motion.section>

      {/* LIVE stats */}
      <motion.section
        initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.04 }}
        className="card p-5 md:p-6 mb-4"
        data-testid="rep-stats"
      >
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            <div className="font-display text-[18px] font-bold text-[hsl(var(--text-strong))]">{t("rep_stats_title")}</div>
            <div className="text-[12px] text-[hsl(var(--text-muted))] mt-0.5">{t("rep_stats_desc")}</div>
          </div>
          <button onClick={dlLeaveCsv} className="btn btn-primary" data-testid="rep-dl-leave">
            <Download size={14} strokeWidth={2} /> {t("download_csv")}
          </button>
        </div>
        {loading && <div className="text-[13px] text-[hsl(var(--text-muted))]" data-testid="rep-stats-loading">{t("rep_loading")}</div>}
        {error && !loading && <div className="text-[13px] text-[hsl(var(--danger))]" data-testid="rep-stats-error">{error}</div>}
        {!loading && !error && stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="rep-stats-grid">
            <StatCard testid="stat-total"    icon={ClipboardList}  label={t("rep_stat_total")}          value={stats.total_requests}       tone="primary" />
            <StatCard testid="stat-approved" icon={CheckCircle2}   label={t("rep_stat_approved")}       value={stats.approved}             tone="success" />
            <StatCard testid="stat-pending"  icon={AlarmClock}     label={t("rep_stat_pending")}        value={stats.pending}              tone="warning" />
            <StatCard testid="stat-rejected" icon={XCircle}        label={t("rep_stat_rejected")}       value={stats.rejected}             tone="danger" />
            <StatCard testid="stat-sick-rep" icon={Thermometer}    label={t("rep_stat_sick_reports")}   value={stats.sick_reports}         tone="danger" />
            <StatCard testid="stat-sick-emp" icon={UsersIcon}      label={t("rep_stat_sick_employees")} value={stats.sick_employees_unique} tone="danger" />
            <StatCard testid="stat-sick-day" icon={Thermometer}    label={t("rep_stat_sick_days")}      value={stats.sick_days_total}      tone="danger" />
          </div>
        )}
      </motion.section>

      {/* Results (desktop table + mobile cards) */}
      <motion.section
        initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.08 }}
        className="card p-5 md:p-6 mb-4"
        data-testid="rep-results"
      >
        <div className="mb-4">
          <div className="font-display text-[18px] font-bold text-[hsl(var(--text-strong))]">{t("rep_results_title")}</div>
          <div className="text-[12px] text-[hsl(var(--text-muted))] mt-0.5">{t("rep_results_desc")}</div>
        </div>
        {loading && <div className="text-[13px] text-[hsl(var(--text-muted))]" data-testid="rep-results-loading">{t("rep_loading")}</div>}
        {!loading && !error && rows.length === 0 && (
          <div className="text-[13px] text-[hsl(var(--text-muted))] py-6 text-center" data-testid="rep-results-empty">
            {t("rep_results_empty")}
          </div>
        )}
        {!loading && !error && rows.length > 0 && (
          <>
            {/* Desktop table */}
            <div className="hidden md:block overflow-x-auto" data-testid="rep-results-table">
              <table className="w-full text-[13px]">
                <thead className="border-b hairline text-left text-[hsl(var(--text-muted))]">
                  <tr>
                    <th className="py-2 pr-3 eyebrow">{t("rep_col_employee")}</th>
                    <th className="py-2 pr-3 eyebrow">{t("rep_col_type")}</th>
                    <th className="py-2 pr-3 eyebrow">{t("rep_col_period")}</th>
                    <th className="py-2 pr-3 eyebrow text-right">{t("rep_col_days_in_period")}</th>
                    <th className="py-2 pr-3 eyebrow">{t("rep_col_status")}</th>
                    <th className="py-2 pr-3 eyebrow">{t("rep_col_team")}</th>
                    <th className="py-2 pr-0 eyebrow">{t("rep_col_location")}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => {
                    const s = statusStyle(r.status);
                    return (
                      <tr key={r._id} className="border-t hairline" data-testid={`rep-row-${r._id}`}>
                        <td className="py-2.5 pr-3 font-semibold text-[hsl(var(--text-strong))]">{displayEmployeeName(r, t)}</td>
                        <td className="py-2.5 pr-3">{t(r.leave_type) || r.leave_type}</td>
                        <td className="py-2.5 pr-3 tabular-nums">{fmtDate(r.start_date)} → {fmtDate(r.end_date)}</td>
                        <td className="py-2.5 pr-3 text-right tabular-nums font-bold">{r.days}</td>
                        <td className="py-2.5 pr-3">
                          <span className="inline-flex items-center h-6 px-2 rounded-full text-[11px] font-semibold" style={{ background: s.bg, color: s.fg }}>
                            {t(`status_${r.status}`) || t(r.status)}
                          </span>
                        </td>
                        <td className="py-2.5 pr-3 text-[hsl(var(--text-muted))]">{r.team_name || "—"}</td>
                        <td className="py-2.5 pr-0 text-[hsl(var(--text-muted))]">{r.location_name || "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {/* Mobile cards */}
            <div className="md:hidden flex flex-col gap-2" data-testid="rep-results-cards">
              {rows.map((r) => {
                const s = statusStyle(r.status);
                return (
                  <div key={r._id} className="rounded-xl border hairline p-3" data-testid={`rep-card-${r._id}`}>
                    <div className="flex items-center justify-between gap-2 mb-1.5">
                      <div className="font-bold text-[13px] text-[hsl(var(--text-strong))] truncate">{displayEmployeeName(r, t)}</div>
                      <span className="inline-flex items-center h-6 px-2 rounded-full text-[11px] font-semibold shrink-0" style={{ background: s.bg, color: s.fg }}>
                        {t(`status_${r.status}`) || t(r.status)}
                      </span>
                    </div>
                    <div className="text-[12px] text-[hsl(var(--text-muted))] flex items-center gap-2 flex-wrap">
                      <span>{t(r.leave_type) || r.leave_type}</span>
                      <span>·</span>
                      <span className="tabular-nums">{fmtDate(r.start_date)} → {fmtDate(r.end_date)}</span>
                      <span>·</span>
                      <span className="text-[hsl(var(--text-strong))] font-bold tabular-nums">{r.days}d</span>
                    </div>
                    {(r.team_name || r.location_name) && (
                      <div className="text-[11.5px] text-[hsl(var(--text-muted))] mt-1 flex gap-2 flex-wrap">
                        {r.team_name && <span>{r.team_name}</span>}
                        {r.location_name && <span>· {r.location_name}</span>}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}
      </motion.section>

      {/* Timesheets */}
      <motion.section
        initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
        className="card p-5 md:p-6 mb-4"
        data-testid="rep-timesheets-card"
      >
        <div className="flex items-start gap-4 mb-4">
          <div className="w-10 h-10 rounded-xl bg-[hsl(var(--primary-soft))] text-[hsl(var(--primary))] grid place-items-center shrink-0">
            <Clock size={16} strokeWidth={1.75} />
          </div>
          <div className="flex-1">
            <div className="font-display text-[16px] font-bold text-[hsl(var(--text-strong))]">{t("timesheets_report")}</div>
            <div className="text-[12px] text-[hsl(var(--text-muted))] mt-0.5">{t("timesheets_report_desc")}</div>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <label className="flex flex-col gap-1.5">
            <span className="text-[12px] font-medium text-[hsl(var(--text-muted))]">{t("status")}</span>
            <select value={tsStatus} onChange={(e) => setTsStatus(e.target.value)} className="input-field" data-testid="rep-ts-status">
              <option value="approved">{t("approved")}</option>
              <option value="submitted">{t("pending")}</option>
              <option value="rejected">{t("rejected")}</option>
              <option value="all">{t("all")}</option>
            </select>
          </label>
        </div>
        <div className="mt-4 flex justify-end">
          <button onClick={dlTimesheets} disabled={busy === "ts"} className="btn btn-primary" data-testid="rep-dl-timesheets">
            <Download size={14} strokeWidth={2} /> {busy === "ts" ? t("working") : t("download_csv")}
          </button>
        </div>
      </motion.section>

      {/* Shifts */}
      <motion.section
        initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.12 }}
        className="card p-5 md:p-6"
        data-testid="rep-shifts-card"
      >
        <div className="flex items-start gap-4 mb-4">
          <div className="w-10 h-10 rounded-xl bg-[hsl(160_65%_92%)] text-[hsl(160_65%_35%)] grid place-items-center shrink-0">
            <CalendarDays size={16} strokeWidth={1.75} />
          </div>
          <div className="flex-1">
            <div className="font-display text-[16px] font-bold text-[hsl(var(--text-strong))]">{t("shifts_report")}</div>
            <div className="text-[12px] text-[hsl(var(--text-muted))] mt-0.5">{t("shifts_report_desc")}</div>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
          <label className="flex flex-col gap-1.5">
            <span className="text-[12px] font-medium text-[hsl(var(--text-muted))]">{t("status")}</span>
            <select value={shStatus} onChange={(e) => setShStatus(e.target.value)} className="input-field" data-testid="rep-sh-status">
              <option value="all">{t("all")}</option>
              <option value="published">{t("published")}</option>
              <option value="draft">{t("draft")}</option>
            </select>
          </label>
        </div>
        <div className="mt-4 flex justify-end">
          <button onClick={dlShifts} disabled={busy === "sh"} className="btn btn-primary" data-testid="rep-dl-shifts">
            <Download size={14} strokeWidth={2} /> {busy === "sh" ? t("working") : t("download_csv")}
          </button>
        </div>
      </motion.section>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, tone, testid }) {
  const styles = {
    primary: { bg: "hsl(var(--primary-soft))", fg: "hsl(var(--primary))" },
    success: { bg: "hsl(var(--success-soft))", fg: "hsl(var(--success))" },
    warning: { bg: "hsl(38 92% 92%)",          fg: "hsl(28 82% 40%)" },
    danger:  { bg: "hsl(var(--danger-soft))",  fg: "hsl(var(--danger))" },
  }[tone] || { bg: "hsl(var(--muted))", fg: "hsl(var(--text-muted))" };
  return (
    <div className="rounded-xl border hairline p-3 flex items-center gap-3" data-testid={testid}>
      <div className="w-9 h-9 rounded-lg grid place-items-center shrink-0" style={{ background: styles.bg, color: styles.fg }}>
        <Icon size={16} strokeWidth={1.9} />
      </div>
      <div className="min-w-0">
        <div className="font-display text-[22px] font-bold text-[hsl(var(--text-strong))] leading-none tabular-nums">{value}</div>
        <div className="text-[11px] text-[hsl(var(--text-muted))] mt-1 truncate">{label}</div>
      </div>
    </div>
  );
}
